import { FC, MouseEvent, ReactNode, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import { MoreIcon } from '../theme/Icons';
import { IonButton, IonItem, IonItemDivider, IonList, IonPopover, IonText } from '@ionic/react';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
    popover: {
        '&::part(content)': {
            width: 'auto',
            minWidth: 200,
            maxWidth: 650
        },

        '& ion-list ion-item ion-text[slot="start"]': {
            marginInlineEnd: 12
        }
    },

    heading: {
        '&::part(native)': {
            fontWeight: 500,
            background: 'var(--ion-color-step-50)'
        }
    }
});

export interface IAction {
    text: string | ReactNode;
    Icon?: any;
    role?: 'destructive';
    'data-type'?: string;
    handler?: () => void;
    hidden?: boolean;
    disabled?: boolean;
    divider?: boolean;
    noLines?: boolean;
    heading?: boolean;
}

export interface IMenuProps {
    actions: IAction[];
    'data-id'?: string;
}

export const MenuList: FC<{actions: IAction[], onActionClicked?: (action: IAction) => void}> = ({ actions, onActionClicked }) => {
    const classes = useStyles();

    const handleClick = async (event: MouseEvent, action: IAction) => {
        event.stopPropagation();
        if (action.handler) {
            action.handler();
            if (onActionClicked) {
                onActionClicked(action);
            }
        }
    };

    return (
        <IonList data-type="menu-list" lines="none" style={{paddingTop: 0, paddingBottom: 0}}>
            {actions.filter(a => !a.hidden).map((action, idx) => {
                const { divider, noLines, heading, text, Icon, role, disabled, 'data-type': dataType } = action;
                return (
                    <div key={`item-${idx}`}>
                        {divider &&
                            <IonItemDivider
                                color="light"
                            >
                                {text}
                            </IonItemDivider>
                        }
                        {!divider &&
                            <IonItem
                                button
                                data-type={dataType}
                                disabled={disabled}
                                lines={idx === actions.length - 1 || noLines ? 'none' : 'full'}
                                className={`${heading ? classes.heading : ''}`}
                                onClick={event => handleClick(event, action)}
                            >
                                {Icon &&
                                    <Icon slot="start" color={role === 'destructive' ? 'danger' : 'medium'} />
                                }
                                <IonText
                                    className="ion-text-nowrap"
                                    style={{paddingRight: 30}}
                                    color={role === 'destructive' ? 'danger' : undefined}
                                >
                                    {text}
                                </IonText>
                            </IonItem>
                        }
                    </div>
                );
            })}
        </IonList>
    );
};

export const Menu: FC<IMenuProps> = ({ actions, 'data-id': dataId }) => {
    const classes = useStyles();

    // generate an id ionic can use to connect the button to the menu
    const idRef = useRef(nanoid());

    const [isOpen, setOpen] = useState(false);

    const onClickMenu = (event: MouseEvent) => {
        event.stopPropagation();
        setOpen(prev => !prev);
    };

    const onActionClicked = () => {
        setOpen(false);
    };

    return (
        <>
            <IonButton
                data-id={dataId}
                data-type="menu-button"
                id={idRef.current}
                shape="round"
                fill="clear"
                style={{'--padding-start': '10px', '--padding-end': '10px', width: 36, height: 36}}
                onClick={onClickMenu}
            >
                <MoreIcon color="dark" />
            </IonButton>

            <IonPopover keepContentsMounted trigger={idRef.current} alignment="end" className={classes.popover} isOpen={isOpen} onDidDismiss={() => setOpen(false)}>
                <MenuList actions={actions} onActionClicked={onActionClicked} />
            </IonPopover>
        </>
    );
};
